import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import Select from "react-select";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/userSlice";
import { usePopup } from "../../utils/PopupContext";
import styles from "./MySlips.module.css";
import {
  Truck,
  CalendarDays,
  Building2,
  MapPin,
  BadgeIndianRupee,
  Search,
  Image as ImageIcon,
  Filter,
  RefreshCw,
  User,
  Clock,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import FloatingActions from "../../components/FloatingActions";

/**
 * MySlips - Station based
 * Shows all slips for stations assigned to the logged-in user.
 *
 * Expected APIs:
 * 1) POST /api/wbauto/getUsersUnloadingStations
 * 2) POST /api/wbauto/getMySlips
 *
 * Each slip row expected fields:
 * {
 *   txn_id, docnumber, truck_no,
 *   item_name/material, item_catg, grade, mine, from_place,
 *   location_code, location_name, unload_location, unloaded_at,
 *   qty_mt,
 *   before_url, after_url,
 *   before_by_name, after_by_name,
 *   before_time/before_captured_at, after_time/after_captured_at,
 *   qc_details: [{ test_code, test_desc, value }],
 *   qc_ded_amt, remarks, created_at
 * }
 */

const MySlips = () => {
  const user = useSelector(selectUser);
  const emp_id = user?.emp_id;
  const token = user?.token;
  const topRef = useRef(null);
  const bottomRef = useRef(null);

  const { showPopup } = usePopup();

  // auth header (same pattern you use in other screens)
  const authCfg = useMemo(() => {
    if (!token) return {};
    return { headers: { Authorization: `Bearer ${token}` } };
  }, [token]);

  // Filters
  const [fromDate, setFromDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(dayjs().format("YYYY-MM-DD")); // ✅ default TODAY
  const [searchText, setSearchText] = useState("");

  // Stations mapped to user
  const [stationOptions, setStationOptions] = useState([]);
  const [selectedStation, setSelectedStation] = useState({
    value: "ALL",
    label: "All My Stations",
  });

  // Data
  const [loadingStations, setLoadingStations] = useState(false);
  const [loading, setLoading] = useState(false);
  const [slips, setSlips] = useState([]);
  const [total, setTotal] = useState(0);

  // Pagination (safe for performance)
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [hasMore, setHasMore] = useState(true);

  // Photo zoom modal
  const [photoModal, setPhotoModal] = useState({
    open: false,
    url: "",
    title: "",
  });

  // ✅ NEW: View mode + one-card navigation
  const [viewMode, setViewMode] = useState("filters"); // filters | cards
  const [cardIndex, setCardIndex] = useState(0);

  // For swipe (optional / safe)
  const touchStartXRef = useRef(null);

  // ---------- helpers ----------
  const fmtDT = (d) => (d ? dayjs(d).format("DD-MMM-YYYY hh:mm A") : "-");
  const money = (n) => {
    const x = Number(n || 0);
    return x.toLocaleString("en-IN", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });
  };

  const buildStationCsv = () => {
    if (!selectedStation || selectedStation.value === "ALL") {
      const ids = stationOptions
        .filter((o) => o.value !== "ALL")
        .map((o) => o.value);

      return ids.join(",");
    }

    return String(selectedStation.value);
  };

  const openPhoto = (url, title) => {
    if (!url) return;
    setPhotoModal({ open: true, url, title });
  };

  const closePhoto = () =>
    setPhotoModal({ open: false, url: "", title: "" });

  // ---------- load stations ----------
  const fetchUnloadingPlaces = async () => {
    if (!emp_id) return;

    try {
      setLoadingStations(true);

      const res = await axios.post(
        "/api/wbauto/getUsersUnloadingStations",
        {
          emp_id,
          token, // kept (even if backend ignores it)
        },
        authCfg
      );

      const data = res.data || [];
      console.log("Stage---0- Stations:", data);

      const formatted = data.map((row) => ({
        value: row.L_PlaceId,
        label: row.L_PlaceName,
        entity_code: row.entity_code,
      }));

      const finalOptions = [
        { value: "ALL", label: "All My Stations" },
        ...formatted,
      ];

      setStationOptions(finalOptions);
      setSelectedStation(finalOptions[0]);
    } catch (err) {
      console.error(
        "Error loading stations:",
        err?.response?.data || err.message
      );
      showPopup?.("Unable to load assigned stations.", "error");
    } finally {
      setLoadingStations(false);
    }
  };

  // ---------- fetch slips ----------
  const fetchSlips = async ({ reset = false } = {}) => {
    if (!emp_id) return;
    if (!fromDate) {
      showPopup?.("Please select From Date.", "warning");
      return;
    }

    const nextPage = reset ? 1 : page;

    setLoading(true);
    try {
      const body = {
        emp_id,
        fromDate,
        toDate: toDate || null,
        station_ids_csv: buildStationCsv(),
        page: nextPage,
        pageSize,
        scope: "stations",
      };

      const resp = await axios.post(
        "/api/wbauto/getMySlips",
        body,
        { ...(authCfg || {}) }
      );

      // Accept either {rows,total} or direct array
      const rows = Array.isArray(resp.data)
        ? resp.data
        : resp.data?.rows || [];

      const tot = Array.isArray(resp.data)
        ? resp.data?.total || 0
        : resp.data?.total ?? 0;

      console.log("backend resp", resp);

      if (reset) {
        setSlips(rows);
        setPage(2);
        setCardIndex(0); // ✅ reset card pointer
      } else {
        setSlips((prev) => [...prev, ...rows]);
        setPage((p) => p + 1);
      }

      const computedTotal = tot || (reset ? rows.length : slips.length + rows.length);
      setTotal(computedTotal);

      const noMore = rows.length < pageSize;
      setHasMore(!noMore);

      // ✅ After load/reload, switch to cards mode (requirement)
      // Only when reset (fresh load). If load more, we keep view mode.
      if (reset) setViewMode("cards");
    } catch (e) {
      console.error("Fetch slips failed:", e?.response?.data || e.message);
      showPopup?.("Could not fetch slips. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // ---------- computed summary ----------
  const filteredSlips = useMemo(() => {
    const q = (searchText || "").trim().toLowerCase();
    if (!q) return slips;

    return slips.filter((s) => {
      const truck = (s.truck_no || "").toLowerCase();
      const doc = (s.docnumber || "").toLowerCase();
      const item = (s.item_name || s.material || "").toLowerCase();
      const mine = (s.mine || "").toLowerCase();
      const grade = (s.grade || "").toLowerCase();
      return (
        truck.includes(q) ||
        doc.includes(q) ||
        item.includes(q) ||
        mine.includes(q) ||
        grade.includes(q)
      );
    });
  }, [slips, searchText]);

  const summary = useMemo(() => {
    const totalCards = filteredSlips.length;
    const ded = filteredSlips.reduce(
      (sum, s) => sum + Number(s.qc_ded_amt || 0),
      0
    );
    return { totalCards, ded };
  }, [filteredSlips]);

  // ✅ current slip in card mode
  const currentSlip = filteredSlips?.[cardIndex] || null;

  // ---------- effects ----------
  useEffect(() => {
    fetchUnloadingPlaces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emp_id]);

  // auto reload when station/date changes (keep your behavior)
  useEffect(() => {
    if (!stationOptions.length) return;
    fetchSlips({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStation?.value, fromDate, toDate]);

  // ✅ Keep cardIndex valid when filters change
  useEffect(() => {
    if (cardIndex >= filteredSlips.length) setCardIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredSlips.length]);

  // ---------- card navigation ----------
  const goPrev = () => setCardIndex((i) => Math.max(0, i - 1));
  const goNext = () =>
    setCardIndex((i) => Math.min(filteredSlips.length - 1, i + 1));

  const onExitCards = () => {
    setViewMode("filters");
    // Keep data in memory; do not clear slips
  };

  // ✅ swipe support
  const onTouchStart = (e) => {
    touchStartXRef.current = e.changedTouches?.[0]?.clientX ?? null;
  };
  const onTouchEnd = (e) => {
    const sx = touchStartXRef.current;
    const ex = e.changedTouches?.[0]?.clientX ?? null;
    if (sx == null || ex == null) return;

    const diff = ex - sx;
    if (Math.abs(diff) < 60) return; // threshold

    if (diff > 0) goPrev();
    else goNext();
  };

  // ---------- UI ----------
  return (
    <div className={styles.page}>
      <div ref={topRef} />
      {/* ===================== STICKY TOP ===================== */}
      <div className={styles.stickyTop}>
        <div className={styles.headerRow}>
          <div className={styles.titleBlock}>
            <div className={styles.title}>My Slips</div>
            <div className={styles.subtitle}>
              All unloading slips from your assigned stations • Photos • QC •
              Deductions • Full audit
            </div>
          </div>

          {/* ✅ Keep your pills, add card position in cards mode */}
          <div className={styles.summaryPills}>
            <div className={styles.pill}>
              <Filter size={16} />
              <span>
                Total Cards: <b>{summary.totalCards}</b>
              </span>
            </div>

            <div className={`${styles.pill} ${styles.pillMoney}`}>
              <BadgeIndianRupee size={16} />
              <span>
                Total Deduction: <b>₹ {money(summary.ded)}</b>
              </span>
            </div>

            <div className={styles.pill}>
              <RefreshCw size={16} />
              <span>
                Loaded: <b>{filteredSlips.length}</b> /{" "}
                <b>{total || filteredSlips.length}</b>
              </span>
            </div>

            {viewMode === "cards" && (
              <div className={`${styles.pill} ${styles.pillPos}`}>
                <span>
                  Card: <b>{filteredSlips.length ? cardIndex + 1 : 0}</b> /{" "}
                  <b>{filteredSlips.length}</b>
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ===================== FILTERS (hidden in card mode) ===================== */}
        {viewMode === "filters" && (
          <div className={`${styles.filterRow} ${styles.filterRowMobileStack}`}>
            <div className={styles.filterItem}>
              <label>
                <CalendarDays size={16} /> From Date
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className={styles.input}
              />
            </div>

            <div className={styles.filterItem}>
              <label>
                <CalendarDays size={16} /> To Date
              </label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className={styles.input}
              />
            </div>

            <div className={styles.filterItemWide}>
              <label>
                <Building2 size={16} /> Station
              </label>
              <Select
                isLoading={loadingStations}
                options={stationOptions}
                value={selectedStation}
                onChange={(v) => setSelectedStation(v)}
                classNamePrefix="rs"
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: "42px",
                    borderRadius: "12px",
                    borderColor: "#d7dde6",
                    boxShadow: "none",
                  }),
                }}
              />
            </div>

            <div className={styles.filterItemWide}>
              <label>
                <Search size={16} /> Search (truck/doc/material/mine/grade)
              </label>
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Type to filter cards..."
                className={styles.input}
              />
            </div>

            <div className={styles.btnRow}>
              <button
                className={styles.primaryBtn}
                onClick={() => fetchSlips({ reset: true })}
                disabled={loading}
              >
                {loading ? "Loading..." : "Reload"}
              </button>

              <button
                className={styles.secondaryBtn}
                onClick={() => {
                  setSearchText("");
                  setToDate(dayjs().format("YYYY-MM-DD")); // ✅ reset to TODAY (new baseline)
                  setFromDate(dayjs().format("YYYY-MM-DD"));
                  setSelectedStation({ value: "ALL", label: "All My Stations" });
                }}
                disabled={loading}
              >
                Reset
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ===================== BODY ===================== */}
      {viewMode === "filters" && (
        <div className={styles.deck}>
          {/* Keep old deck behavior in filter view */}
          {filteredSlips.length === 0 && !loading && (
            <div className={styles.empty}>No slips found for the selected filters.</div>
          )}

          {filteredSlips.map((s, idx) => (
            <div key={s.txn_id || `${s.docnumber}_${idx}`} className={styles.card}>
              {/* Card Top */}
              <div className={styles.cardTop}>
                <div className={styles.cardLeft}>
                  <div className={styles.rowLine}>
                    <Truck size={18} />
                    <div className={styles.bigText}>{s.truck_no || "-"}</div>
                    <span className={styles.badge}>DOC: {s.docnumber || "-"}</span>
                    {s.unloading_method && (
                      <span className={styles.badgeSoft}>{s.unloading_method}</span>
                    )}
                  </div>

                  <div className={styles.metaGrid}>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>Material</span>
                      <span className={styles.metaVal}>{s.item_name || s.material || "-"}</span>
                    </div>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>Grade</span>
                      <span className={styles.metaVal}>{s.grade || s.item_grade || "-"}</span>
                    </div>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>Mine</span>
                      <span className={styles.metaVal}>{s.mine || s.mine_name || "-"}</span>
                    </div>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>
                        <MapPin size={14} /> From
                      </span>
                      <span className={styles.metaVal}>{s.from_place || s.load_point || "-"}</span>
                    </div>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>
                        <MapPin size={14} /> Unloaded At
                      </span>
                      <span className={styles.metaVal}>
                        {s.location_name || s.unload_location || "-"}
                      </span>
                    </div>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>Qty (MT)</span>
                      <span className={styles.metaVal}>{s.qty_mt ?? s.qty ?? "-"}</span>
                    </div>
                  </div>
                </div>

                <div className={styles.cardRight}>
                  <div
                    className={`${styles.dedBox} ${Number(s.qc_ded_amt || 0) > 0 ? styles.dedRed : styles.dedGreen
                      }`}
                  >
                    <div className={styles.dedLabel}>Deduction</div>
                    <div className={styles.dedAmt}>₹ {money(s.qc_ded_amt || 0)}</div>
                  </div>
                </div>
              </div>

              {/* Photos Row */}
              <div className={styles.photoRow}>
                <div className={styles.photoBlock}>
                  <div className={styles.photoHeader}>
                    <ImageIcon size={16} />
                    <span>Before Photo</span>
                  </div>

                  <div
                    className={`${styles.photoBox} ${s.before_url ? "" : styles.photoEmpty}`}
                    onClick={() => s.before_url && openPhoto(s.before_url, "Before Photo")}
                    role="button"
                    tabIndex={0}
                  >
                    {s.before_url ? (
                      <img className={styles.photoThumb} src={s.before_url} alt="Before Photo" />
                    ) : (
                      <div className={styles.photoPlaceholder}>No Photo</div>
                    )}
                  </div>
                  <div className={styles.auditLine}>
                    <div className={styles.auditItem}>
                      <User size={14} /> {s.before_by_name || "-"}
                    </div>
                    <div className={styles.auditItem}>
                      <Clock size={14} /> {fmtDT(s.before_captured_at || s.before_time || s.before_captured_datetime)}
                    </div>
                  </div>
                </div>

                <div className={styles.photoBlock}>
                  <div className={styles.photoHeader}>
                    <ImageIcon size={16} />
                    <span>After Photo</span>
                  </div>

                  <div
                    className={`${styles.photoBox} ${s.after_url ? "" : styles.photoEmpty}`}
                    onClick={() => s.after_url && openPhoto(s.after_url, "After Photo")}
                    role="button"
                    tabIndex={0}
                  >
                    {s.after_url ? (
                      <img className={styles.photoThumb} src={s.after_url} alt="After Photo" />
                    ) : (
                      <div className={styles.photoPlaceholder}>No Photo</div>
                    )}
                  </div>

                  <div className={styles.auditLine}>
                    <div className={styles.auditItem}>
                      <User size={14} /> {s.after_by_name || "-"}
                    </div>
                    <div className={styles.auditItem}>
                      <Clock size={14} /> {fmtDT(s.after_captured_at || s.after_time || s.after_captured_datetime)}
                    </div>
                  </div>
                </div>
              </div>

              {/* QC + Remarks */}
              <div className={styles.bottomRow}>
                <div className={styles.qcBox}>
                  <div className={styles.qcTitle}>QC Parameters</div>

                  {Array.isArray(s.qc_details) && s.qc_details.length > 0 ? (
                    <div className={styles.qcGrid}>
                      {s.qc_details.map((q, qi) => (
                        <div key={`${q.test_code || qi}`} className={styles.qcChip}>
                          <span className={styles.qcCode}>{q.test_code || "-"}</span>
                          <span className={styles.qcDesc}>{q.test_desc || ""}</span>
                          <span className={styles.qcVal}>{q.value ?? "-"}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.qcEmpty}>No QC parameters found.</div>
                  )}
                </div>

                <div className={styles.remBox}>
                  <div className={styles.remTitle}>Remarks</div>
                  <div className={styles.remText}>{s.remarks || "-"}</div>

                  <div className={styles.timeFoot}>
                    <span className={styles.smallMuted}>
                      Created: {fmtDT(s.created_at || s.created_datetime || s.txn_created_at)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Load More (kept) */}
          <div className={styles.loadMoreWrap}>
            {hasMore && filteredSlips.length > 0 && (
              <button
                className={styles.loadMoreBtn}
                onClick={() => fetchSlips({ reset: false })}
                disabled={loading}
              >
                {loading ? "Loading..." : `Load More (${pageSize})`}
              </button>
            )}
            {!hasMore && filteredSlips.length > 0 && (
              <div className={styles.endNote}>All slips loaded.</div>
            )}
          </div>
        </div>
      )}

      {/* ===================== CARD MODE (one card at a time) ===================== */}
      {viewMode === "cards" && (
        <div className={styles.deck}>
          {(!currentSlip || filteredSlips.length === 0) && !loading && (
            <div className={styles.empty}>No slips found for the selected filters.</div>
          )}

          {currentSlip && (
            <div
              className={styles.card}
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
            >
              {/* Card Top */}
              <div className={styles.cardTop}>
                <div className={styles.cardLeft}>
                  <div className={styles.rowLine}>
                    <Truck size={18} />
                    <div className={styles.bigText}>{currentSlip.truck_no || "-"}</div>
                    <span className={styles.badge}>DOC: {currentSlip.docnumber || "-"}</span>
                    {currentSlip.unloading_method && (
                      <span className={styles.badgeSoft}>{currentSlip.unloading_method}</span>
                    )}
                  </div>

                  <div className={styles.metaGrid}>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>Material</span>
                      <span className={styles.metaVal}>
                        {currentSlip.item_name || currentSlip.material || "-"}
                      </span>
                    </div>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>Grade</span>
                      <span className={styles.metaVal}>
                        {currentSlip.grade || currentSlip.item_grade || "-"}
                      </span>
                    </div>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>Mine</span>
                      <span className={styles.metaVal}>
                        {currentSlip.mine || currentSlip.mine_name || "-"}
                      </span>
                    </div>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>
                        <MapPin size={14} /> From
                      </span>
                      <span className={styles.metaVal}>
                        {currentSlip.from_place || currentSlip.load_point || "-"}
                      </span>
                    </div>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>
                        <MapPin size={14} /> Unloaded At
                      </span>
                      <span className={styles.metaVal}>
                        {currentSlip.location_name || currentSlip.unload_location || "-"}
                      </span>
                    </div>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>Qty (MT)</span>
                      <span className={styles.metaVal}>
                        {currentSlip.qty_mt ?? currentSlip.qty ?? "-"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={styles.cardRight}>
                  <div
                    className={`${styles.dedBox} ${Number(currentSlip.qc_ded_amt || 0) > 0
                        ? styles.dedRed
                        : styles.dedGreen
                      }`}
                  >
                    <div className={styles.dedLabel}>Deduction</div>
                    <div className={styles.dedAmt}>₹ {money(currentSlip.qc_ded_amt || 0)}</div>
                  </div>
                </div>
              </div>

              {/* Photos Row */}
              <div className={styles.photoRow}>
                <div className={styles.photoBlock}>
                  <div className={styles.photoHeader}>
                    <ImageIcon size={16} />
                    <span>Before Photo</span>
                  </div>

                  <div
                    className={`${styles.photoBox} ${currentSlip.before_url ? "" : styles.photoEmpty
                      }`}
                    onClick={() =>
                      currentSlip.before_url &&
                      openPhoto(currentSlip.before_url, "Before Photo")
                    }
                    role="button"
                    tabIndex={0}
                  >
                    {currentSlip.before_url ? (
                      <img className={styles.photoThumb} src={currentSlip.before_url} alt="Before Photo" />
                    ) : (
                      <div className={styles.photoPlaceholder}>No Photo</div>
                    )}
                  </div>
                  <div className={styles.auditLine}>
                    <div className={styles.auditItem}>
                      <User size={14} /> {currentSlip.before_by_name || "-"}
                    </div>
                    <div className={styles.auditItem}>
                      <Clock size={14} />{" "}
                      {fmtDT(currentSlip.before_captured_at || currentSlip.before_time || currentSlip.before_captured_datetime)}
                    </div>
                  </div>
                </div>

                <div className={styles.photoBlock}>
                  <div className={styles.photoHeader}>
                    <ImageIcon size={16} />
                    <span>After Photo</span>
                  </div>

                  <div
                    className={`${styles.photoBox} ${currentSlip.after_url ? "" : styles.photoEmpty
                      }`}
                    onClick={() =>
                      currentSlip.after_url &&
                      openPhoto(currentSlip.after_url, "After Photo")
                    }
                    role="button"
                    tabIndex={0}
                  >
                    {currentSlip.after_url ? (
                      <img className={styles.photoThumb} src={currentSlip.after_url} alt="After Photo" />
                    ) : (
                      <div className={styles.photoPlaceholder}>No Photo</div>
                    )}
                  </div>

                  <div className={styles.auditLine}>
                    <div className={styles.auditItem}>
                      <User size={14} /> {currentSlip.after_by_name || "-"}
                    </div>
                    <div className={styles.auditItem}>
                      <Clock size={14} />{" "}
                      {fmtDT(currentSlip.after_captured_at || currentSlip.after_time || currentSlip.after_captured_datetime)}
                    </div>
                  </div>
                </div>
              </div>

              {/* QC + Remarks */}
              <div className={styles.bottomRow}>
                <div className={styles.qcBox}>
                  <div className={styles.qcTitle}>QC Parameters</div>

                  {Array.isArray(currentSlip.qc_details) &&
                    currentSlip.qc_details.length > 0 ? (
                    <div className={styles.qcGrid}>
                      {currentSlip.qc_details.map((q, qi) => (
                        <div key={`${q.test_code || qi}`} className={styles.qcChip}>
                          <span className={styles.qcCode}>{q.test_code || "-"}</span>
                          <span className={styles.qcDesc}>{q.test_desc || ""}</span>
                          <span className={styles.qcVal}>{q.value ?? "-"}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.qcEmpty}>No QC parameters found.</div>
                  )}
                </div>

                <div className={styles.remBox}>
                  <div className={styles.remTitle}>Remarks</div>
                  <div className={styles.remText}>{currentSlip.remarks || "-"}</div>

                  <div className={styles.timeFoot}>
                    <span className={styles.smallMuted}>
                      Created:{" "}
                      {fmtDT(
                        currentSlip.created_at ||
                        currentSlip.created_datetime ||
                        currentSlip.txn_created_at
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ✅ Floating buttons */}
          <div className={styles.fabBar}>
            <button
              className={styles.fabBtn}
              onClick={goPrev}
              disabled={cardIndex <= 0}
              title="Previous"
            >
              <ChevronLeft size={18} />
              Prev
            </button>

            <button
              className={styles.fabBtn}
              onClick={goNext}
              disabled={cardIndex >= filteredSlips.length - 1}
              title="Next"
            >
              Next
              <ChevronRight size={18} />
            </button>

            <button className={`${styles.fabBtn} ${styles.fabExit}`} onClick={onExitCards} title="Exit">
              <X size={18} />
              Exit
            </button>
          </div>
        </div>
      )}

      {/* Photo Modal (kept) */}
      {photoModal.open && (
        <div className={styles.modalBackdrop} onClick={closePhoto}>
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.modalTitle}>{photoModal.title}</div>
              <button className={styles.modalClose} onClick={closePhoto}>
                ✕
              </button>
            </div>
            <div className={styles.modalBody}>
              <img src={photoModal.url} alt="zoom" className={styles.modalImg} />
            </div>
          </div>
        </div>
      )}

      <FloatingActions topRef={topRef} bottomRef={bottomRef} />
      <div ref={bottomRef} />
    </div>
  );
};

export default MySlips;